import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Tabs, Alert, Spin } from 'antd';
import StreamBuilder from './StreamBuilder';
import StreamScheduler from './StreamScheduler';
import { YouTubeLiveStream, Video, YouTubeStreamVideo } from './types';
import { getStreamWithVideos } from './api';
import OverlayBuilder from './OverlayBuilder';
import ScheduleChunksTab from './ScheduleChunksTab';

export type StreamEditorTabKey = 'details' | 'configure' | 'overlay' | 'schedule';

interface StreamEditorModalProps {
  open: boolean;
  stream?: YouTubeLiveStream | null;
  initialTab?: StreamEditorTabKey;
  distributionPlatformData?: any[];
  onClose: () => void;
  onSave: () => void;
}

const mapStreamVideos = (videos?: YouTubeStreamVideo[]): Video[] => {
  if (!videos) return [];
  return videos.map((sv: YouTubeStreamVideo) => ({
    id: sv.video_id,
    ref_id: sv.video_file_name || '',
    title: sv.video_title || '',
    duration: sv.duration_seconds,
    url: sv.video_url || '',
    file_name: sv.video_file_name || '',
    language: '',
    created_at: sv.created_at
  }));
};

const StreamEditorModal: React.FC<StreamEditorModalProps> = ({
  open,
  stream,
  initialTab = 'details',
  distributionPlatformData = [],
  onClose,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState<StreamEditorTabKey>(initialTab);
  const [currentStream, setCurrentStream] = useState<YouTubeLiveStream | null>(stream || null);
  const [loadingStream, setLoadingStream] = useState(false);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab);
    setCurrentStream(stream || null);
  }, [open, initialTab, stream]);

  const ensureStreamWithVideos = async () => {
    if (!currentStream?.id) return;
    if (currentStream.videos) return;
    setLoadingStream(true);
    try {
      const fullStream = await getStreamWithVideos(currentStream.id);
      setCurrentStream(fullStream);
    } catch (error) {
      console.error('Error loading stream details:', error);
    } finally {
      setLoadingStream(false);
    }
  };

  const handleTabChange = async (key: string) => {
    const nextTab = key as StreamEditorTabKey;
    setActiveTab(nextTab);
    if (nextTab === 'configure') {
      await ensureStreamWithVideos();
    }
  };

  const handleDetailsSaved = async (streamId: string) => {
    try {
      const fullStream = await getStreamWithVideos(streamId);
      setCurrentStream(fullStream);
      onSave();
    } catch (error) {
      console.error('Error refreshing stream after save:', error);
      onSave();
    }
  };

  const handleConfigureSaved = () => {
    onSave();
  };

  const configureDisabled = !currentStream?.id;

  const schedulerVideos = useMemo(() => mapStreamVideos(currentStream?.videos), [currentStream]);

  return (
    <Modal
      title="Stream"
      open={open}
      onCancel={onClose}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'details',
            label: 'Details',
            children: (
              <StreamBuilder
                stream={currentStream}
                distributionPlatformData={distributionPlatformData}
                onClose={onClose}
                onSaved={handleDetailsSaved}
              />
            )
          },
          {
            key: 'configure',
            label: 'Configure',
            disabled: configureDisabled,
            children: configureDisabled ? (
              <Alert
                type="info"
                message="Save stream details to configure"
                description="Create the stream first, then return here to configure OAuth or manual settings."
                showIcon
              />
            ) : loadingStream ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <Spin />
              </div>
            ) : currentStream ? (
              <StreamScheduler
                stream={currentStream}
                videos={schedulerVideos}
                onSchedule={handleConfigureSaved}
                onCancel={onClose}
              />
            ) : null
          },
          {
            key: "overlay",
            label: "Overlay",
            disabled: false,
            children: <OverlayBuilder
              stream={currentStream}
              onClose={onClose}
              onSaved={handleDetailsSaved}
                />,
          },
          // Schedule tab: only for Amagi HLS-pull streams (imported schedule
          // chunks replace catalog video management).
          ...(currentStream?.output_target === 'amagi_hls' ? [{
            key: 'schedule',
            label: 'Schedule',
            disabled: !currentStream?.id,
            children: !currentStream?.id ? (
              <Alert
                type="info"
                message="Save stream details first"
                description="Create the stream, then return here to import its schedule."
                showIcon
              />
            ) : (
              <ScheduleChunksTab stream={currentStream} onChanged={onSave} />
            )
          }] : [])
        ]}
      />
    </Modal>
  );
};

export default StreamEditorModal;
